//================================================================
//  Component: Manage Request
//================================================================

//  Purpose: Cancel or escalate a request in mobile view

//  Properties:
//    - requestDoc = {Object, contains request details}

//  Example:
//  <ManageRequest 
//    requestDoc={requestDoc}
//  ></ManageRequest>

//================================================================


//Libraries
import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../Library/WriteDocument';
import AddRemoveDocumentArray from '../../Library/AddRemoveDocumentArray';

//Images
import ArrowIcon from '../../Components/Images/Icon_LeftArrow_Black.svg';
import TickIcon from '../../Components/Images/Icon_Approved_Green.svg';
import ErrorIcon from '../../Components/Images/Icon_Error_Red.svg'

export default function ManageRequest({ requestDoc }) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Stages for different pages > 'pre-selection', 'selection', 'post-selection', 'error'
  const [stage, setStage] = useState('pre-selection');

  // For changing request status dropdown > 'Approve', 'Reassign', 'Reject'
  const [requestStatus, setRequestStatus] = useState('');

  // Some status changes require an additional comment
  const [changeStatusComment, setChangeStatusComment] = useState('');

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Submits form
  function handleSubmit() {

    if (requestStatus === 'Escalate') {

      setRequestStatus('');
      setChangeStatusComment('');
      setStage('post-selection');

      return;
    }

    if (requestStatus === 'Cancel') {

      const docPromises = [];

      // Updated Status
      const docObject = {
          'status': 'Cancelled',
          'cancellationreason': changeStatusComment,
          'lastmodifiedby': {
              'email': getUser.emailaddress,
              'givenname': getUser.givenname,
              'surname': getUser.surname,
          },
          'lastmodifieddate': new Date(),
      };

      docPromises.push(
          WriteDocument('requests', requestDoc?.requestid, docObject, true)
      );

      // Updated Activity Feed
      const updatedActivityFeed =  {
          'action': 'cancelled',
          'actionedby': {
              'email': getUser.emailaddress,
              'givenname': getUser.givenname,
              'surname': getUser.surname,
          },
          'activitydate': new Date(),
          'comments': changeStatusComment
      };

      docPromises.push(
          AddRemoveDocumentArray('requests', requestDoc?.requestid, 'activityfeed', updatedActivityFeed, 'add', updatedActivityFeed.actionedby)
      );

      return Promise.all(docPromises)
      .then(() => {

        setRequestStatus('');
        setChangeStatusComment('');
        setStage('post-selection');

      })
      .catch((error) => {

        console.log(error);
        setStage('error')

      })
      
    }
  }

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  // Returns nothing if request has already been approved/rejected in the past.
  if (!['Not Started', 'In Progress'].includes(requestDoc?.status) && stage !== 'post-selection') return;

  return (
    <>

      {/* =========================================================== */}
      {/* PRE-SELECTION STAGE: ONLY SHOW BUTTON                       */}
      {/* =========================================================== */}

      <div
        className='fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-300 w-full py-4 px-4 z-10'
        hidden={stage !== 'pre-selection'}
      >
        <button className='w-full bg-black text-white py-2 rounded-md font-medium' onClick={() => {
          setStage('selection');
          setRequestStatus('');
        }}
        >
          Manage Request
        </button>

      </div>

      {/* =========================================================== */}
      {/*  SELECTION STAGE                                            */}
      {/* =========================================================== */}

      <div className='absolute flex flex-col h-[calc(100vh-70px)] w-full top-[70px] z-10 bg-white' hidden={stage !== 'selection'}>
        <div className='flex flex-col h-full justify-between'>

          {/* BODY */}
          <div>

            {/* HEADER */}
            <div className='flex flex-row px-4 py-3 border-b-[1px] border-neutral-300'>

              {/* Back: Returns to previous screen, does not clear Text field */}
              <img src={ArrowIcon} alt='Back' className='h-6' onClick={() => { setStage('pre-selection') }}></img>
              <h5 className='m-auto'>Manage Request</h5>

            </div>

            {/* BODY */}
            <div className='flex flex-col gap-4 p-4'>

              {/* Change Status */}
              <div className='flex flex-col gap-3'>

                <label className='font-semibold mb-0'>Change status:</label>
                <select className='Input-Field-Select' value={requestStatus} disabled={!['Not Started', 'In Progress'].includes(requestDoc?.status)} onChange={(e) => setRequestStatus(e.target.value)}>
                  
                  {/* If Completed, show as "Approved", otherwise show "Rejected". If pending approval, show "Select" */}
                  <option hidden value=''>Select</option>
                  {/* <option value='Escalate'>Escalate</option> */}
                  {/* <option value='Reassign'>Reassign</option> */}
                  <option className='text-[#DE0000]' value='Cancel'>Cancel</option>

                </select>
                
              </div>


              {/* Comment */}
              <div className='flex flex-col gap-3' hidden={requestStatus.length === 0}>

                <label className='font-semibold mb-0'> 
                  Comment: <span className='text-[#C4314B]'>*</span> 
                </label>

                <textarea
                  className='Input-Field-TextArea'
                  placeholder={`Provide a reason for ${requestStatus === 'Escalate' ? 'escalating' : 'cancelling'} this request`}
                  value={changeStatusComment}
                  onChange={(e) => setChangeStatusComment(e.target.value)}
                ></textarea>

              </div>

            </div>

          </div>

          {/* FOOTER */}
          <div className='fixed bottom-0 border-t-2 w-full py-3 px-4 flex flex-row gap-2 justify-evenly'>

            <button className='Primary-Button w-[150px]' disabled={changeStatusComment === ''} onClick={() => handleSubmit()}>
              Proceed
            </button>

            {/* Cancel: Returns to previous screen, clears the Text field. */}
            <button className='Secondary-Button w-[150px]' onClick={() => {
              setRequestStatus('');
              setChangeStatusComment('');
              setStage('pre-selection');
            }}>
              Cancel
            </button>

          </div>

        </div>
      </div>

      {/* =========================================================== */}
      {/*  POST-SELECTION STAGE: SHOW SUCCESS TOAST                   */}
      {/* =========================================================== */}
      <div className='fixed bottom-0 left-0 right-0 flex flex-col justify-between w-full h-[150px] rounded-lg bg-white shadow-lg z-10  p-3' hidden={stage !== 'post-selection'}>

        <div className='flex flex-row gap-2 p-3 bg-[#e7f2da] rounded-md'>
          <img src={TickIcon} alt='Success'></img>
          <label>Successfully updated request.</label>
        </div>

        <div className='Submit-Button !mr-0' onClick={() => setStage('pre-selection')}>
          Close
        </div>

      </div>

      {/* =========================================================== */}
      {/* ERROR: SHOW FAILURE TOAST                                   */}
      {/* =========================================================== */}
      <div className='fixed bottom-0 left-0 right-0 flex flex-col justify-between w-full h-[170px] rounded-lg bg-white shadow-lg z-10  p-3' hidden={stage !== 'error'}>

        <div className='flex flex-row gap-2 p-3 py-2 w-full bg-[#FFEBEE] rounded-md items-center justify-center'>
                  <img className='w-[25px] h-[25px]' src={ErrorIcon} alt='Error'></img>
                  <label className='font-[450]'>Something went wrong. Please try again or <Link className='underline' target='_blank' to='/help'>get help</Link>.</label>
        </div>

        <div className='Submit-Button' onClick={() => setStage('pre-selection')}>
          Close
        </div>

      </div>


    </>

  )
}
